import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { applyPreviousQuote, getRFQDuplicate } from "../../../../api/client";
import { queryKeys } from "../../../../api/queryKeys";
import { toast } from "../../../../utils/notifications";
import type { RFQ } from "../../../../types/rfq";
import type { TeamMember } from "../../../../types";

/**
 * Duplicate-RFQ detection: has this item set (same item names, any
 * quantities) already been quoted - often to a different company? Looked up
 * when the row is expanded; any failure just means no banner. Applying copies
 * that quote's prices and remarks onto this RFQ.
 */
export function useDuplicateQuote(rfq: RFQ, expanded: boolean, selectedUser: TeamMember | null) {
  const queryClient = useQueryClient();

  const { data: duplicateData } = useQuery({
    queryKey: queryKeys.rfqDuplicate(rfq.rfq_id),
    queryFn: () => getRFQDuplicate(rfq.rfq_id),
    enabled: expanded && rfq.status !== "QUOTED" && (rfq.items || []).length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const duplicate = duplicateData?.duplicate ?? null;

  const applyMutation = useMutation({
    mutationFn: () => {
      if (!duplicate) throw new Error("No duplicate quote to apply");
      return applyPreviousQuote(rfq.rfq_id, duplicate.priorRfqId, selectedUser?.name);
    },
    onSuccess: (result) => {
      toast({
        title: "Previous quote applied",
        description: `${result.applied} item(s) priced from RFQ #${duplicate?.priorRfqId}. Review and hit Send Quote.`,
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.rfqDuplicate(rfq.rfq_id) });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not apply previous quote",
        description: (error instanceof Error && error.message) || "Please refresh and try again.",
        severity: "error",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });
    },
  });

  return { duplicate, apply: () => applyMutation.mutate(), applying: applyMutation.isPending };
}

export type DuplicateQuote = ReturnType<typeof useDuplicateQuote>;
